import type { MarginReportChartInput } from './types'

/**
 * ハイブリッドスキーム対象かの判定。
 * 「detail が存在する最新日」の保証発行上限金額 > 0。
 * guaranteeIssuanceLimit は変換層で detail の無い日に null が入るため、
 * 末尾から null を飛ばして遡る（配列末尾の日付が detail 無しでもその日では判定しない）。
 * entries は日付昇順で渡される前提。
 */
export function isHybridMarginReport(
  entries: readonly MarginReportChartInput[],
): boolean {
  const latest = entries.findLast(
    (
      entry,
    ): entry is MarginReportChartInput & {
      guaranteeIssuanceLimit: number
    } => entry.guaranteeIssuanceLimit !== null,
  )

  return latest !== undefined && latest.guaranteeIssuanceLimit > 0
}

export type MarginReportDerivedEntry = {
  coverageAmount: number | null // カバー額
  shortage: number | null // 不足
  refundableSurplus: number | null // 余剰金（返金可能）
  maxTradableAmount: number | null // 最大取引可能金額
  tradableRemaining: number | null // 取引可能残枠
  surplusShortage: number | null // 「余剰/不足」列
}

export function deriveMarginReportEntry(
  entry: MarginReportChartInput,
  isHybrid: boolean,
): MarginReportDerivedEntry {
  const {
    deposits,
    totalCreditAmount,
    requiredMargin,
    surplus,
    guaranteeIssuanceLimit,
  } = entry

  const coverageAmount = isHybrid ? totalCreditAmount : deposits

  const shortage =
    requiredMargin === null || coverageAmount === null
      ? null
      : Math.max(requiredMargin - coverageAmount, 0)

  // surplus には dev-tool 専用の上書き列があるため、deposits − requiredMargin で
  // 再計算せず backend から返された surplus をそのまま使う（backend 側も意図的な非対称とコメントしている。
  // 根拠: backend/app/internal/usecases/marginreport/list_margin_reports.go:105）。
  const refundableSurplus = surplus === null ? null : Math.max(surplus, 0)

  const maxTradableAmount =
    deposits === null || guaranteeIssuanceLimit === null
      ? null
      : deposits + guaranteeIssuanceLimit

  const tradableRemaining =
    maxTradableAmount === null || requiredMargin === null
      ? null
      : maxTradableAmount - requiredMargin

  // shortage が null（requiredMargin かカバー額が不明）で不足を判定できない日は、
  // 余剰金側の値を出す（意図的なフォールバック）。
  const surplusShortage =
    shortage !== null && shortage > 0 ? -shortage : refundableSurplus

  return {
    coverageAmount,
    shortage,
    refundableSurplus,
    maxTradableAmount,
    tradableRemaining,
    surplusShortage,
  }
}
